import type { ReactNode } from "react";
import {
  Edit2,
  Image as ImageIcon,
  Key,
  Mail,
  MessageSquare,
  Pause,
  Play,
  Shield,
  UserCheck,
  UserMinus,
  UserPlus,
  UserX,
  type LucideIcon,
} from "lucide-react";
import type { ActivityItem } from "./types";

export type RenderedEntry = {
  icon: LucideIcon;
  phrase: ReactNode;
  time: string;
};

const ICON_BY_ACTION: Record<string, LucideIcon> = {
  "project.created": Edit2,
  "project.renamed": Edit2,
  "project.suspended": Pause,
  "project.unsuspended": Play,
  "project.config_updated": Edit2,
  "project.icon_updated": ImageIcon,
  "api_key.rotated": Key,
  "invite.sent": Mail,
  "invite.revoked": Mail,
  "invite.resent": Mail,
  "invite.accepted": UserCheck,
  "team.member_removed": UserMinus,
  "player.updated": UserPlus,
  "guild.updated": Shield,
  "guild.member_promoted": Shield,
  "guild.member_demoted": Shield,
  "guild.leadership_transferred": Shield,
  "guild.member_kicked": UserX,
  "chat.message_posted": MessageSquare,
  "chat.message_deleted": MessageSquare,
};

const FALLBACK_ICON: LucideIcon = Edit2;

function pickIcon(action: string): LucideIcon {
  return ICON_BY_ACTION[action] ?? FALLBACK_ICON;
}

function targetLabel(item: ActivityItem, fallback = "?"): string {
  return item.target.label ?? fallback;
}

function detail<T = unknown>(item: ActivityItem, key: string): T | undefined {
  return item.details?.[key] as T | undefined;
}

function formatTime(iso: string): string {
  const t = new Date(iso);
  const now = Date.now();
  const diffMs = now - t.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)} min ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)} h ago`;
  if (diffMs < 2 * day) {
    return `Yesterday · ${t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  return t.toLocaleDateString([], { month: "short", day: "numeric" });
}

const s = (text: string) => <strong>{text}</strong>;

function phraseFor(item: ActivityItem): ReactNode {
  switch (item.action) {
    case "project.created":
      return (
        <>
          created project «{s(detail<string>(item, "name") ?? targetLabel(item))}»
        </>
      );
    case "project.renamed": {
      const oldName = detail<string>(item, "old_name") ?? "";
      const newName = detail<string>(item, "new_name") ?? "";
      return (
        <>
          renamed project «{s(oldName)}» → «{s(newName)}»
        </>
      );
    }
    case "project.suspended":
      return <>suspended project «{s(targetLabel(item))}»</>;
    case "project.unsuspended":
      return <>unsuspended project «{s(targetLabel(item))}»</>;
    case "project.config_updated": {
      const keys = detail<string[]>(item, "changed_keys") ?? [];
      return (
        <>
          updated project config (
          {keys.map((k, i) => (
            <span key={k}>
              {i > 0 ? ", " : ""}
              <code>{k}</code>
            </span>
          ))}
          )
        </>
      );
    }
    case "project.icon_updated":
      return <>updated project icon</>;
    case "api_key.rotated":
      return (
        <>
          rotated API key (ending {s(detail<string>(item, "new_last4") ?? "")})
        </>
      );
    case "invite.sent":
      return <>invited {s(detail<string>(item, "email") ?? targetLabel(item))}</>;
    case "invite.revoked":
      return (
        <>
          revoked invite for {s(detail<string>(item, "email") ?? targetLabel(item))}
        </>
      );
    case "invite.resent":
      return (
        <>
          resent invite to {s(detail<string>(item, "email") ?? targetLabel(item))}
        </>
      );
    case "invite.accepted":
      return <>accepted invite</>;
    case "team.member_removed":
      return (
        <>
          removed {s(detail<string>(item, "email") ?? targetLabel(item))} from project
        </>
      );
    case "player.updated": {
      const changes = (detail<string[]>(item, "changes") ?? []).join(", ");
      const suffix = changes ? ` (${changes})` : "";
      return (
        <>
          updated player {s(targetLabel(item))}
          {suffix}
        </>
      );
    }
    case "guild.updated": {
      const changes = (detail<string[]>(item, "changes") ?? []).join(", ");
      const suffix = changes ? ` (${changes})` : "";
      return (
        <>
          updated guild «{s(targetLabel(item))}»{suffix}
        </>
      );
    }
    case "guild.member_promoted":
      return (
        <>
          promoted {s(detail<string>(item, "player_display") ?? "?")} to officer in «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.member_demoted":
      return (
        <>
          demoted {s(detail<string>(item, "player_display") ?? "?")} to member in «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.leadership_transferred":
      return (
        <>
          made {s(detail<string>(item, "player_display") ?? "?")} leader of «
          {s(targetLabel(item))}»
        </>
      );
    case "guild.member_kicked":
      return (
        <>
          kicked {s(detail<string>(item, "player_display") ?? "?")} from «
          {s(targetLabel(item))}»
        </>
      );
    case "chat.message_posted":
      return <>posted system message in «{s(targetLabel(item))}»</>;
    case "chat.message_deleted": {
      const author = detail<string>(item, "author_display");
      const suffix = author ? ` (${author})` : "";
      return (
        <>
          deleted a message in «{s(targetLabel(item))}»{suffix}
        </>
      );
    }
    default:
      return (
        <>
          performed unknown action <code>{item.action}</code>
        </>
      );
  }
}

export function renderEntry(item: ActivityItem): RenderedEntry {
  return {
    icon: pickIcon(item.action),
    phrase: phraseFor(item),
    time: formatTime(item.created_at),
  };
}
